import type { TabularSheetCellValue } from '../../shared/tabular-sheet-payload.model';

/** One column in the agreements Excel grid: stable key + display header. */
export interface AgreementSheetColumnSpec<K extends string = string> {
    readonly key: K;
    readonly header: string;
}

/**
 * Single source of truth for agreements sheet column order, keys, and headers.
 * {@link AgreementDataRow} and {@link mapAgreementApiRecordToDataRow} must stay aligned with this list.
 */
export const AGREEMENT_SHEET_COLUMNS = [
    { key: 'agreementNumber', header: 'Agreement Number' },
    { key: 'status', header: 'Status' },
    { key: 'yourReference', header: 'Your Reference' },
    { key: 'agreementType', header: 'Agreement Type' },
    { key: 'description', header: 'Description' },
    { key: 'startDate', header: 'Start Date' },
    { key: 'expiryDate', header: 'Expiry Date' },
    { key: 'isCod', header: 'Is COD' },
    { key: 'originOrganisation', header: 'Origin Organisation' },
    { key: 'originPartnerId', header: 'Origin Partner Id' },
    { key: 'originFacility', header: 'Origin Facility' },
    { key: 'originFacilityCode', header: 'Origin Facility Code' },
    { key: 'destinationOrganisation', header: 'Destination Organisation' },
    { key: 'destinationPartnerId', header: 'Destination Partner Id' },
    { key: 'destinationFacility', header: 'Destination Facility' },
    { key: 'destinationFacilityCode', header: 'Destination Facility Code' },
    { key: 'averageTransportCost', header: 'Average Transport Cost' },
    { key: 'transportCurrency', header: 'Transport Currency' },
    { key: 'co2Emissions', header: 'CO2 Emissions' },
    { key: 'co2Uom', header: 'CO2 UOM' },
    { key: 'isUnverified', header: 'Is Unverified' },
] as const;

export type AgreementColumnKey = (typeof AGREEMENT_SHEET_COLUMNS)[number]['key'];

/** One logical row after adapting a Get-Agreements record; keys match {@link AGREEMENT_SHEET_COLUMNS}. */
export type AgreementDataRow = Record<AgreementColumnKey, TabularSheetCellValue>;

/** Excel header row strings in column order (derived from {@link AGREEMENT_SHEET_COLUMNS}). */
export const AGREEMENT_TABLE_HEADERS: readonly string[] = AGREEMENT_SHEET_COLUMNS.map(
    (c) => c.header
);
